import { COLORS } from '@/config/colors';
import { WEAPONS, type WeaponRarity } from '@/config/weapons';
import {
  WEAPON_CATEGORY_COLOR,
  WEAPON_RARITY_COLOR,
  weaponCrateKey,
} from '@/config/weapon-visuals';
import { css, fillOutlined, circle, shade } from './draw-utils';
import { drawWeaponGlyph } from './weapon-glyphs';
import type { ProceduralDef } from '../types';

const WOOD = 0x6b5a3e;
/** Rarity pips on the lid — a shape cue that survives colour blindness */
const RARITY_PIPS: Record<WeaponRarity, number> = { common: 1, rare: 2, epic: 3 };

/**
 * Weapon crate drop — supply box carrying the WEAPON's own glyph on the lid,
 * a rarity-coloured rim and 1-3 rarity pips (docs/09). One asset per weapon
 * id so the three bullet guns (etc.) no longer look identical.
 */
function makeCrate(weaponId: string): ProceduralDef {
  const def = WEAPONS[weaponId]!;
  return {
    key: weaponCrateKey(weaponId),
    width: 72,
    height: 72,
    anchorX: 0.5,
    anchorY: 0.5,
    frames: 1,
    draw(ctx, w, h) {
      const cx = w / 2;
      const cy = h / 2 - 2;
      const s = 23; // half box size
      const rim = WEAPON_RARITY_COLOR[def.rarity];
      const accent = WEAPON_CATEGORY_COLOR[def.category];

      // Drop shadow under the box
      ctx.beginPath();
      ctx.ellipse(cx, cy + s + 4, s * 1.05, 5, 0, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(10, 6, 20, 0.35)';
      ctx.fill();

      // Rarity rim: a coloured band outside the dark outline
      ctx.beginPath();
      ctx.roundRect(cx - s - 4, cy - s - 4, (s + 4) * 2, (s + 4) * 2, 10);
      ctx.strokeStyle = css(rim);
      ctx.lineWidth = 4;
      ctx.stroke();

      // Box body
      ctx.beginPath();
      ctx.roundRect(cx - s, cy - s, s * 2, s * 2, 7);
      fillOutlined(ctx, WOOD, 4);

      // Lid plank + side rivets
      ctx.beginPath();
      ctx.roundRect(cx - s, cy - s, s * 2, 9, [7, 7, 0, 0]);
      ctx.fillStyle = css(shade(WOOD, 1.25));
      ctx.fill();
      ctx.fillStyle = css(shade(WOOD, 0.7));
      for (const sx of [-1, 1]) {
        ctx.beginPath();
        ctx.roundRect(cx + sx * s - (sx > 0 ? 5 : 0), cy - s + 11, 5, s * 2 - 13, 2);
        ctx.fill();
      }

      // Rarity pips on the lid plank
      const pips = RARITY_PIPS[def.rarity];
      for (let i = 0; i < pips; i++) {
        circle(ctx, cx + (i - (pips - 1) / 2) * 8, cy - s + 4.5, 2.6);
        ctx.fillStyle = css(rim);
        ctx.fill();
      }

      // Dark panel carrying the weapon glyph (category tinted edge). Wave 4:
      // bigger glyph + thicker category edge — the crate must read as THE
      // same weapon the bag, sockets, HUD and toast show (docs/09)
      ctx.beginPath();
      ctx.roundRect(cx - 18, cy - 12, 36, 34, 6);
      ctx.fillStyle = css(COLORS.outline);
      ctx.fill();
      ctx.strokeStyle = css(accent);
      ctx.lineWidth = 3;
      ctx.stroke();

      drawWeaponGlyph(ctx, weaponId, cx, cy + 5, 34);
      void h;
    },
  };
}

/** `pickup/crate-<weaponId>` for every weapon in the table. */
export const crateDefs: ProceduralDef[] = Object.keys(WEAPONS).map(makeCrate);
